import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, makeStyles, Tab, Tabs, Theme, Breadcrumbs, Link, IconButton, Divider, Box } from '@material-ui/core';
import { EmailOutlined, FileCopy } from '@material-ui/icons';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import DoneIcon from '@material-ui/icons/CheckCircleOutlined';
import ScheduleIcon from '@material-ui/icons/AvTimerOutlined';
import LightbulbIcon from '@material-ui/icons/EmojiObjectsOutlined';
import WorkOutlineRoundedIcon from '@material-ui/icons/WorkOutlineRounded';
import LiveHelpIcon from '@material-ui/icons/LiveHelpOutlined';
import StatusChip from '../../components/StatusChip';
import Button from '../../components/Button';
import { StyledText } from '../../components/StyledText';
import { ConnectorDeal, ConnectorDealStatus, ConnectorReferralOption, EntrepreneurProfileBody, Role, LoadingStatus, DealInfoForTopbar } from '../../types/types';
import api, { ApiError, isApiError } from '../../api/api';
import { ConnectorContextProvider, ConnectorDealsContextProvider, useAlert, useConnector, useUser } from '../../contexts';
import ConnectorReferralModal from '../connector/ConnectorReferralModal';
import ConnectorDealMoveToButton from '../connector/deals/ConnectorDealMoveToButton';
import ConnectorGuide from '../../components/PublishingGuide/ConnectorGuide';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(0),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3)
  },
  investorRoot: {
    margin: theme.spacing(0),
    padding: 25,
    paddingLeft: 0,
    paddingBottom: 0
  },
  matchedInvestorsRoot: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    marginTop: '50px',
    marginBottom: '35px'
  },

  title: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    margin: theme.spacing(2.5, 0, 4, 0),
    alignItems: 'center'
  },

  instructionBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: '12px'
  },

  instructionTitle: {
    margin: 0,
    marginTop: '20px',
    verticalAlign: 'center',
    display: 'inline-flex'
  },

  instructionIcon: {
    padding: 0,
    marginLeft: '10px'
  },

  tutorialLink: {
    display: 'flex'
  },

  hiddenContent: {
    display: 'none'
  },

  regularContent: {
  },

  tutorialIcon: {
    marginRight: '2px'
  },
  accent: {
    color: theme.palette.accent.main
  },
  statusContainer: {
    marginLeft: '27px',
    display: 'flex',
    alignItems: 'center'
  },
  statusText: {
    marginLeft: '12px'
  },
  divider: {
    padding: 0,
    margin: '-1px 0 0'
  },
  centerItems: {
    display: 'flex',
    alignItems: 'center'
  }
}));

const useTabStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: 0,
    minHeight: 10,
    '& .MuiTab-root': {
      margin: 0,
      padding: 0,
      minHeight: 0
    },
    '& .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row',
      fontFamily: theme.typography.fontFamily,
      fontSize: 20,
      fontWeight: 500,
      color: '#212632',
      textTransform: 'none'
    },
    '& .MuiTab-wrapper > *:first-child': {
      margin: 10
    },
    '& .Mui-selected .MuiTab-wrapper > *:first-child': {
      fill: theme.palette.primary.main
    }
  }
}));
interface ReferralOptionsState {
  forEnt?: number
  loadingStatus: LoadingStatus
  investors: ConnectorReferralOption[]
}
interface ConnectorViewProps {
  profile?: EntrepreneurProfileBody,
  shareableLinkElement: JSX.Element | null,
  dealInfo: DealInfoForTopbar,
  setDealInfo: React.Dispatch<React.SetStateAction<DealInfoForTopbar>>
}
const initialReferralOptionsState = { forEnt: undefined, loadingStatus: LoadingStatus.Idle, investors: [] };
const ConnectorView:React.FC<ConnectorViewProps> = ({
  profile,
  shareableLinkElement,
  dealInfo,
  setDealInfo
}) => {
  const { getAccessToken, userInfo } = useUser();
  const { profile: connectorProfile, fetchProfile, shouldShowIncompleteProfileGuide, canSendReferrals } = useConnector();
  const styles = useStyles();
  const tabStyles = useTabStyles();
  const history = useHistory();
  const { postNewAlert } = useAlert();
  const { url } = useRouteMatch();
  const { pathname } = useLocation();
  const [referralOptionsState, setReferralOptionsState] = useState<ReferralOptionsState>(initialReferralOptionsState);
  const [pendingReferral, setPendingReferral] = useState<boolean>(false);
  const { deal } = dealInfo;

  const entProfileId = profile?.id;

  const tabValue = useMemo(() => {
    if (pathname.includes('matched-investors')) {
      return `${url}/matched-investors`;
    } else {
      return `${url}`;
    }
  }, [pathname, url]);

  const handleTabChange = (event: React.ChangeEvent<{}>, value: string) => {
    if (value.includes('matched-investors')) {
      history.push(`${value}/requested`);
    } else {
      history.push(`${value}`);
    }
  };

  const handleSendReferralRequest = async (values: any) => {
    if (!pendingReferral || !profile) {
      console.error('Not logged in or missing entrepreneur profile');
      return;
    }

    const investors = values.selectedInvestors.map((v:ConnectorReferralOption) => (
      { email: v.email, name: v.contactName, id: v.investorProfileId }
    ));
    const { referralMessage, additionalEmails } = values;
    const input = { investors, entId: profile.id, message: referralMessage, additionalRecipients: additionalEmails };

    try {
      const { success, dealStatusUpdated } = await api.sendDealReferrals(await getAccessToken(), input);
      console.log('sent referrals success:', success);
      setPendingReferral(false);
      if (dealStatusUpdated) {
        handleStatusChange(ConnectorDealStatus.InProgress);
      }
    } catch (e: unknown) {
      if (isApiError(e)) {
        const { message } = (e as ApiError);
        postNewAlert(message);
      }
    }
  };

  const handleStatusChange = (newStatus: ConnectorDealStatus) => {
    if (!deal) {
      return;
    }
    const newDeal: ConnectorDeal = { ...deal, status: newStatus };
    setDealInfo({ ...dealInfo, deal: newDeal });
  };

  const handleOpenProfile = async () => {
    history.push(`/connector/${connectorProfile.id}`);
  };

  useEffect(() => {
    const getConnectorReferralOptions = async (entrepreneurProfileId: number) => {
      setReferralOptionsState({ forEnt: entrepreneurProfileId, loadingStatus: LoadingStatus.Loading, investors: [] });
      try {
        const options = await api.getConnectorReferralOptions(await getAccessToken(), entrepreneurProfileId);
        setReferralOptionsState({ forEnt: entrepreneurProfileId, loadingStatus: LoadingStatus.Complete, investors: options });
      } catch (e: unknown) {
        console.error(e);
        setReferralOptionsState({ forEnt: entrepreneurProfileId, loadingStatus: LoadingStatus.Complete, investors: [] });
        postNewAlert('An error occurred getting referral options.');
      }
    };
    if (entProfileId !== undefined && entProfileId >= 0) {
      if (entProfileId !== referralOptionsState.forEnt || referralOptionsState.loadingStatus === LoadingStatus.Idle) {
        getConnectorReferralOptions(entProfileId);
      }
    } else {
      setReferralOptionsState(initialReferralOptionsState);
    }
  }, [getAccessToken, postNewAlert, entProfileId, referralOptionsState]);

  useEffect(() => {
    const getDeal = async (profileId: number) => {
      setDealInfo({ forEnt: profileId, loadingStatus: LoadingStatus.Loading, deal: undefined });
      try {
        const connectorDeal = await api.getConnectorDealByEntProfile(await getAccessToken(), profileId);
        setDealInfo({ forEnt: profileId, loadingStatus: LoadingStatus.Complete, deal: connectorDeal });
      } catch (e: unknown) {
        console.error(e);
        setDealInfo({ forEnt: profileId, loadingStatus: LoadingStatus.Complete, deal: undefined });
        postNewAlert('An error occurred fetching deal.');
      }
    };
    if (entProfileId !== undefined && entProfileId >= 0) {
      if (entProfileId !== dealInfo.forEnt || dealInfo.loadingStatus === LoadingStatus.Idle) {
        getDeal(entProfileId);
      }
    } else {
      setDealInfo(initialReferralOptionsState);
    }
  }, [getAccessToken, postNewAlert, entProfileId, dealInfo.forEnt, dealInfo.loadingStatus, setDealInfo]);

  useEffect(() => {
    if (userInfo && userInfo.profileId && connectorProfile.id !== userInfo.profileId) {
      fetchProfile(userInfo.profileId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorProfile, userInfo]);

  if (!profile) {
    return null;
  }

  const iconForStatus = {
    [ConnectorDealStatus.New]: LightbulbIcon,
    [ConnectorDealStatus.InProgress]: ScheduleIcon,
    [ConnectorDealStatus.Done]: DoneIcon
  };
  const IconComponent = deal ? iconForStatus[deal.status] : 'div';

  const textForStatus = {
    [ConnectorDealStatus.New]: 'New',
    [ConnectorDealStatus.InProgress]: 'In progress',
    [ConnectorDealStatus.Done]: 'Done'
  };

  return (
    <Grid id='entrepreneur-top-bar' container wrap='nowrap' direction='column' justify='center' className={styles.root}>
      { shareableLinkElement }
      {referralOptionsState.investors.length > 0 && (
        <ConnectorReferralModal
          open={pendingReferral}
          referralOptions={referralOptionsState.investors}
          onClose={() => { setPendingReferral(false); }}
          handleSubmit={handleSendReferralRequest}
        />
      )}
      <Grid item style={{ marginTop: 10 }}>
        <Breadcrumbs separator='>'>
          <StyledText
            variant='breadcrumbs'
            color='secondary'
            style={{ cursor: 'pointer' }}
            onClick={() => history.push('/connector/deals')}
          >
            Deals
          </StyledText>
          <StyledText variant='breadcrumbs'>{profile?.name}</StyledText>
        </Breadcrumbs>
        <div className={styles.centerItems}>
          <div className={styles.title}>
            <StyledText variant='h1' style={{ lineHeight: '50.4px' }}>
              {profile?.name}
            </StyledText>
            {deal
              && (
              <div className={styles.statusContainer}>
                <IconComponent className={styles.accent} />
                <StyledText variant='investorStatus' color='primary' className={styles.statusText}>
                  {textForStatus[deal.status]}
                </StyledText>
              </div>
              )}
          </div>
          <div style={{ display: 'flex' }}>
            {deal && (
            <ConnectorDealsContextProvider>
              <ConnectorDealMoveToButton deal={deal} onDealStatusUpdate={handleStatusChange} />
            </ConnectorDealsContextProvider>
            )}
            {
              (referralOptionsState.investors.length > 0) && (
                <Button
                  size='small'
                  style={{ marginLeft: 10 }}
                  onClick={() => setPendingReferral(true)}
                  disabled={!canSendReferrals}
                >
                  Make Referral
                </Button>
              )
            }
          </div>
        </div>
        {shouldShowIncompleteProfileGuide
          && (
          <ConnectorGuide
            profile={connectorProfile}
            handleClick={handleOpenProfile}
          />
          )}
        { dealInfo.deal
        && (
        <Tabs
          indicatorColor='primary'
          classes={tabStyles}
          onChange={handleTabChange}
          value={tabValue}
          aria-label='entrepreneur-page-tab'
        >
          <Tab disableFocusRipple disableRipple icon={<AccountBoxIcon />} label='Profile' value={`${url}`} />
          <Tab disableFocusRipple disableRipple icon={<WorkOutlineRoundedIcon />} label='Investors' value={`${url}/matched-investors`} />
        </Tabs>
        )}
        <Divider className={styles.divider} />
      </Grid>
    </Grid>
  );
};

interface EntViewProps {
  profile?: EntrepreneurProfileBody
  shareableLinkElement: JSX.Element | null
  isEditable: boolean
  loading: boolean
}
const EntView:React.FC<EntViewProps> = ({
  profile,
  shareableLinkElement,
  isEditable,
  loading
}) => {
  const styles = useStyles();
  const history = useHistory();
  const isPublished = profile?.isPublished || false;

  return (
    <Grid id='entrepreneur-top-bar' container wrap='nowrap' direction='column' justify='center' className={styles.root}>
      { shareableLinkElement }
      <Grid item>
        <div className={styles.centerItems}>
          <div className={styles.title}>
            <StyledText variant='h1' color={profile?.name ? 'textPrimary' : 'placeholder'}>
              {profile?.name || 'Company Name'}
            </StyledText>
            <StatusChip
              style={(isEditable && !loading) ? { marginLeft: 20 } : { display: 'none' }}
              status={isPublished ? 'Published' : 'Incomplete'}
            />
          </div>
          <div className={loading ? styles.hiddenContent : styles.regularContent}>
            {
              isEditable && (
              <StyledText variant='textButton' color='secondary' component='div'>
                <Button variant='text' onClick={() => window.open('https://www.capitalraise.net/entrepreneur-tutorial')} className={styles.tutorialLink} color='inherit'>
                  <LiveHelpIcon className={styles.tutorialIcon} />
                  &nbsp;Open Entrepreneur Tutorial&nbsp;
                  <KeyboardArrowRightIcon fontSize='small' style={{ height: '25px' }} />
                </Button>
              </StyledText>
              )
            }
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

interface UnknownRoleViewProps {
  profile: EntrepreneurProfileBody
}
const UnkownRoleView:React.FC<UnknownRoleViewProps> = ({
  profile
}) => {
  const styles = useStyles();
  const { name } = profile;

  return (
    <Grid id='entrepreneur-top-bar' container wrap='nowrap' direction='column' justify='center' className={styles.investorRoot}>
      <Grid item>
        <div className={styles.centerItems}>
          <div className={styles.title}>
            <StyledText variant='h1'>
              {name}
            </StyledText>
          </div>
        </div>
      </Grid>
    </Grid>
  );
};


interface EntrepreneurTopbarProps {
  profile?: EntrepreneurProfileBody
  loading: boolean
  isEditable: boolean
  handlePublishProfile?: (data: any) => Promise<void>
  dealInfo: DealInfoForTopbar
  setDealInfo: React.Dispatch<React.SetStateAction<DealInfoForTopbar>>
}

const EntrepreneurTopbar: React.FC<EntrepreneurTopbarProps> = ({
  profile,
  loading,
  isEditable,
  dealInfo,
  setDealInfo
}) => {
  const { userInfo } = useUser();
  const role = userInfo?.role;

  const [copiedAlertOn, setCopiedAlertOn] = useState(false);

  const performCopy = useCallback(() => {
    if (copiedAlertOn) { return; }
    navigator.clipboard.writeText(profile?.shareableLink || '').then(() => {
      setCopiedAlertOn(true);
      window.setTimeout(() => { setCopiedAlertOn(false); }, 1250);
    });
  }, [profile?.shareableLink, copiedAlertOn]);

  const SharableLink = useCallback(() => {
    if (profile && profile.shareableLink) {
      return (
        <Box display='flex' flexDirection='row' alignItems='center'>
          <StyledText>
            {'Share a link to this page: '}
          </StyledText>
          <StyledText color='primary'>
            {profile.shareableLink}
          </StyledText>
          <IconButton
            color='primary'
            onClick={performCopy}
          >
            { copiedAlertOn ? <StyledText color='primary'>Copied!</StyledText> : <FileCopy /> }
          </IconButton>
        </Box>
      );
    } else {
      return null;
    }
  }, [profile, performCopy, copiedAlertOn]);

  if (!profile) {
    return null;
  }

  if (role === Role.Connector) {
    return (
      <ConnectorContextProvider>
        <ConnectorView
          profile={profile}
          shareableLinkElement={SharableLink()}
          dealInfo={dealInfo}
          setDealInfo={setDealInfo}
        />
      </ConnectorContextProvider>
    );
  } else if (role === Role.Ent) {
    return (
      <EntView
        profile={profile}
        shareableLinkElement={SharableLink()}
        loading={loading}
        isEditable={isEditable}
      />
    );
  } else {
    return (
      <UnkownRoleView
        profile={profile}
      />
    );
  }
};

export default EntrepreneurTopbar;
